import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { WERT_OHNE_KOMMA, type StoffWert, type StoffWerteArt } from "@repo/shared";

import { Bestaetigen } from "../components/stoffe/Bestaetigen";
import { api, errorMessage } from "../lib/api";

const MELDUNG_MS = 3000;

const TEXTE: Record<StoffWerteArt, { titel: string; neu: string; leer: string; beispiel: string }> = {
  typ: { titel: "Stoff-Typen", neu: "Neuer Typ", leer: "Noch keine Typen.", beispiel: "z. B. Thermo" },
  material: { titel: "Materialien", neu: "Neues Material", leer: "Noch keine Materialien.", beispiel: "z. B. Poly" },
};

const stoffe = (n: number) => `${n} ${n === 1 ? "Stoff" : "Stoffe"}`;

function listenQuery(state: unknown): string {
  const liste = (state as { liste?: unknown } | null)?.liste;
  return typeof liste === "string" && (liste === "" || liste.startsWith("?")) ? liste : "";
}

/**
 * Gepflegte Liste der Stoff-Typen bzw. Materialien wie im früheren ERP: anlegen, umbenennen (wirkt auf
 * alle Stoffe) und löschen (die API lehnt ab, solange ein Stoff den Wert trägt).
 */
export function StoffWertePage({ art }: { art: StoffWerteArt }) {
  const t = TEXTE[art];
  // Der Listen-Zustand aus der Stoffliste (Filter, Seite) geht beim Zurück nicht verloren.
  const liste = listenQuery(useLocation().state);
  const [werte, setWerte] = useState<StoffWert[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState("");
  const [legtAn, setLegtAn] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<{ id: string; name: string } | null>(null);
  const [benenntUm, setBenenntUm] = useState(false);
  const [loeschen, setLoeschen] = useState<StoffWert | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const meldungTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(meldungTimer.current), []);

  const melde = useCallback((text: string) => {
    setFehler(null);
    setMeldung(text);
    window.clearTimeout(meldungTimer.current);
    meldungTimer.current = window.setTimeout(() => setMeldung(null), MELDUNG_MS);
  }, []);

  const zeigeFehler = useCallback((text: string) => {
    window.clearTimeout(meldungTimer.current);
    setMeldung(null);
    setFehler(text);
  }, []);

  const laden = useCallback(async () => {
    try {
      const res = await api.api.admin["stoff-werte"][":art"].$get({ param: { art } });
      if (!res.ok) throw new Error(await errorMessage(res, "Werte nicht ladbar"));
      const d = await res.json();
      if ("werte" in d) setWerte(d.werte);
      setLadeFehler(null);
    } catch (cause) {
      setLadeFehler(cause instanceof Error ? cause.message : "Werte nicht ladbar");
    }
  }, [art]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function anlegen(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = neu.trim();
    if (!name || legtAn) return;
    if (name.includes(",")) return zeigeFehler(WERT_OHNE_KOMMA);
    setLegtAn(true);
    try {
      const res = await api.api.admin["stoff-werte"][":art"].$post({ param: { art }, json: { name } });
      if (!res.ok) throw new Error(await errorMessage(res, "Anlegen fehlgeschlagen"));
      setNeu("");
      setFehler(null);
      await laden();
    } catch (cause) {
      zeigeFehler(cause instanceof Error ? cause.message : "Anlegen fehlgeschlagen");
    } finally {
      setLegtAn(false);
    }
  }

  async function umbenennen() {
    if (!bearbeitet || benenntUm) return;
    const name = bearbeitet.name.trim();
    const alt = werte?.find((w) => w.id === bearbeitet.id);
    if (!name) return zeigeFehler("Name ist erforderlich.");
    if (name.includes(",")) return zeigeFehler(WERT_OHNE_KOMMA);
    if (alt && alt.name === name) return setBearbeitet(null);
    setBenenntUm(true);
    try {
      const res = await api.api.admin["stoff-werte"][":art"][":id"].$patch({
        param: { art, id: bearbeitet.id },
        json: { name },
      });
      if (!res.ok) throw new Error(await errorMessage(res, "Umbenennen fehlgeschlagen"));
      const d = await res.json();
      setBearbeitet(null);
      if ("aktualisiert" in d) melde(`Umbenannt – ${stoffe(d.aktualisiert)} angepasst.`);
      await laden();
    } catch (cause) {
      zeigeFehler(cause instanceof Error ? cause.message : "Umbenennen fehlgeschlagen");
    } finally {
      setBenenntUm(false);
    }
  }

  async function loeschenAusfuehren() {
    if (!loeschen) return;
    try {
      const res = await api.api.admin["stoff-werte"][":art"][":id"].$delete({ param: { art, id: loeschen.id } });
      if (res.ok) melde("Gelöscht.");
      else zeigeFehler(await errorMessage(res, "Löschen fehlgeschlagen"));
    } catch {
      zeigeFehler("Löschen fehlgeschlagen");
    }
    setLoeschen(null);
    await laden();
  }

  return (
    <section className="stoff-werte">
      <p className="breadcrumb">
        <Link to={{ pathname: "/stoffe", search: liste }}>← Stoffe</Link>
      </p>
      <h1>{t.titel}</h1>
      <p className="muted">Werte, die von Stoffen verwendet werden, können nicht gelöscht werden.</p>

      <form className="stoff-werte__neu" onSubmit={anlegen}>
        <input
          type="text"
          className="stoffe-feld stoffe-feld--gefuellt"
          placeholder={t.beispiel}
          aria-label={t.neu}
          maxLength={100}
          value={neu}
          onChange={(e) => setNeu(e.target.value)}
        />
        <button type="submit" className="primary stoffe-pill" disabled={!neu.trim() || legtAn}>
          {t.neu}
        </button>
      </form>

      <div className="stoffe-statuszeile" role="status">
        {meldung}
      </div>
      {(fehler || ladeFehler) && (
        <p className="error" role="alert">
          {fehler ?? ladeFehler}
        </p>
      )}

      {werte === null ? (
        !ladeFehler && <p className="muted">Lade …</p>
      ) : (
        <div className="tabelle-rahmen stoff-werte__rahmen">
          <table className="table stoff-werte__tabelle">
            <thead>
              <tr>
                <th>Name</th>
                <th className="num">Stoffe</th>
                <th className="stoffe-aktionen-kopf">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {werte.length === 0 ? (
                <tr className="stoffe-leer">
                  <td colSpan={3}>{t.leer}</td>
                </tr>
              ) : (
                werte.map((w) => {
                  const imBearbeiten = bearbeitet?.id === w.id;
                  return (
                    <tr key={w.id}>
                      <td>
                        {imBearbeiten ? (
                          <input
                            type="text"
                            className="stoffe-feld stoffe-feld--gefuellt"
                            aria-label={`Neuer Name für „${w.name}“`}
                            maxLength={100}
                            autoFocus
                            value={bearbeitet.name}
                            onChange={(e) => setBearbeitet({ id: w.id, name: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                void umbenennen();
                              }
                              if (e.key === "Escape") setBearbeitet(null);
                            }}
                          />
                        ) : (
                          w.name
                        )}
                      </td>
                      <td className="num">{w.verwendungen}</td>
                      <td>
                        <span className="stoffe-aktionen">
                          {imBearbeiten ? (
                            <>
                              <button
                                type="button"
                                className="stoffe-aktion primary"
                                disabled={benenntUm || !bearbeitet.name.trim()}
                                onClick={() => void umbenennen()}
                              >
                                Speichern
                              </button>
                              <button type="button" className="stoffe-aktion" onClick={() => setBearbeitet(null)}>
                                Abbrechen
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="stoffe-aktion"
                                onClick={() => setBearbeitet({ id: w.id, name: w.name })}
                              >
                                Umbenennen
                              </button>
                              <button
                                type="button"
                                className="stoffe-aktion danger"
                                disabled={w.verwendungen > 0}
                                title={
                                  w.verwendungen > 0
                                    ? `Wird von ${w.verwendungen} ${w.verwendungen === 1 ? "Stoff" : "Stoffen"} verwendet`
                                    : undefined
                                }
                                onClick={() => setLoeschen(w)}
                              >
                                Löschen
                              </button>
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Bestaetigen
        offen={loeschen !== null}
        titel={loeschen ? `„${loeschen.name}“ löschen?` : ""}
        bestaetigen="Löschen"
        gefaehrlich
        onBestaetigen={loeschenAusfuehren}
        onAbbrechen={() => setLoeschen(null)}
      />
    </section>
  );
}
